import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'
import ColorPicker, { type ColorPickerProps } from './ColorPicker'
import { Label } from '../ui/label'
import { Typography } from '@/components/ui/typography'
import FieldInfo from './FieldInfo'

export type ColorInputProps = ColorPickerProps & {
  id?: string
  label?: ReactNode
  description?: ReactNode
  labelEnd?: ReactNode
  readOnlyEmptyText?: ReactNode
  showFieldInfo?: boolean
}

export default function ColorInput({
  id,
  label = null,
  description = null,
  labelEnd = null,
  className = '',
  readOnlyEmptyText = 'No color selected',
  disabled = false,
  showFieldInfo = true,
  ...props
}: ColorInputProps) {
  const field = useFieldContext<string | null | undefined>()
  const { isReadOnly } = useIsReadOnly()

  const displayValue = field.state.value || readOnlyEmptyText

  return (
    <div className={cn('flex flex-col gap-y-2 w-full', className)}>
      {!!label && (
        <div className="flex flex-col gap-1">
          <div className="inline-flex items-center">
            <Label htmlFor={id || field.name}>{label}</Label>
            {labelEnd}
          </div>
          {!!description && (
            <Typography variant="label" tone="muted">
              {description}
            </Typography>
          )}
        </div>
      )}

      {isReadOnly ? (
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border border-border"
            style={{ backgroundColor: field.state.value || '#ffffff' }}
          />
          <Typography variant="label" tone={!field.state.value ? 'muted' : 'default'}>
            {displayValue}
          </Typography>
        </div>
      ) : (
        <ColorPicker disabled={disabled} {...props} />
      )}

      {showFieldInfo && <FieldInfo />}
    </div>
  )
}
